import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiSearch } from '@assets/js/api/commonApi.js';
import CompChatbot from '@components/form/fragment/comp-chatbot.js';

/**
 * 토너먼트 시스템 관리자 메인 화면
 */
const SysContent = () => {
    const navigate = useNavigate();
    const [dashboardData, setDashboardData] = useState({
        competitionCount: 0,
        playerCount: 0,
        ongoingTournaments: 0,
        completedCompetitions: 0
    });
    const [chatbotOpen, setChatbotOpen] = useState(false);

    // 대시보드 데이터 조회
    const loadDashboardData = () => {
        // API 호출 예시 (실제 API에 맞게 수정 필요)
        Promise.all([
            ApiSearch('/sys/main/search', {}),
            // ApiSearch('/sys/plymng/search/list', {}),
        ]).then(([dashRes]) => {
            if (dashRes && dashRes.success) {
                const competitions = dashRes.data.data.dashInfoMap || [];
                setDashboardData(prev => ({
                    ...prev,
                    competitionCount: competitions.comptnCnt || 0,
                    ongoingTournaments: competitions.mtmkgDCnt || 0,
                    completedCompetitions: competitions.mtmkgECnt || 0
                }));
                setDashboardData(prev => ({
                    ...prev,
                    playerCount: competitions.plyrCnt || 0
                }));
            }
        });
    };

    useEffect(() => {
        loadDashboardData();
    }, []);

    // 메뉴 정보 정의
    const steps = [
        {
            step: 1,
            title: '기본정보 등록',
            description: '토너먼트의 기초 데이터 설정',
            color: '#0d6efd',
            icon: 'bi-gear-fill',
            menus: [
                { label: '선수관리', path: '/sys/bs/player-mng', icon: 'bi-person-badge' },
                { label: '부문관리', path: '/sys/bs/division-mng', icon: 'bi-tag' },
                { label: '체급관리', path: '/sys/bs/weightClass-mng', icon: 'bi-speedometer2' },
                { label: '서포터즈관리', path: '/sys/bs/supporters-mng', icon: 'bi-people' }
            ]
        },
        {
            step: 2,
            title: '대회정보 관리',
            description: '대회 등록, 대회 신청, 토너먼트 관리',
            color: '#198754',
            icon: 'bi-trophy-fill',
            menus: [
                { label: '대회관리', path: '/sys/cm/competition-mng', icon: 'bi-calendar-event' },
                { label: '대회신청양식관리', path: '/sys/cm/competitionForm-mng', icon: 'bi-file-earmark-text' },
                { label: '대회신청관리', path: '/sys/cm/competitionApply-mng', icon: 'bi-clipboard-check' },
                { label: '대진토너먼트관리', path: '/sys/cm/matchtrmnt-mng', icon: 'bi-diagram-3' }
            ]
        },
        {
            step: 3,
            title: '상태정보 관리',
            description: '토너먼트 진행 결과 관리',
            color: '#6f42c1',
            icon: 'bi-clipboard-data-fill',
            menus: [
                { label: '대진선수정보', path: '/sys/cm/matchdtl-mng', icon: 'bi-person-lines-fill' },
                { label: '대진토너먼트정보', path: '/sys/cm/matchtrmnt-mng', icon: 'bi-clipboard-check' },
                { label: '대진결과정보', path: '/sys/cm/matchResult-mng', icon: 'bi-bar-chart-line' },
                { label: '상장', path: '/sys/cm/matchResult-mng', icon: 'bi-award' },
            ]
        }
    ];

    // 단축 액션 버튼
    const quickActions = [
        { label: '대회/대진 등록', path: '/sys/cm/competition-mng', icon: 'bi-plus-circle', color: '#0d6efd' },
        { label: '대회 양식 등록', path: '/sys/cm/competitionForm-mng', icon: 'bi-file-earmark-text', color: '#dc3545' },
        { label: '대회 신청 확인', path: '/sys/cm/competitionApply-mng', icon: 'bi-clipboard-check', color: '#198754' },
        { label: '토너먼트 생성', path: '/sys/cm/matchmaking-mng', icon: 'bi-diagram-3', color: '#6f42c1' },
    ];

    // 메뉴 이동
    const handleMenuClick = (path) => {
        navigate(path);
    };

    return (
        <div className="sys-content-wrapper">
            {/* 헤더 */}
            <div className="sys-header">
                <div className="container-fluid">
                    <div className="header-content">
                        <div>
                            <h1 className="sys-title">
                                <i className="bi bi-speedometer2 me-3"></i>
                                Tournament Management System
                            </h1>
                            <p className="sys-subtitle">토너먼트 운영 관리 시스템 v1.0</p>
                        </div>
                        <div className="header-actions">
                            <button className="btn btn-outline-primary btn-sm">
                                <i className="bi bi-question-circle me-1"></i>
                                도움말
                            </button>
                            <button className="btn btn-outline-info btn-sm">
                                <i className="bi bi-bell me-1"></i>
                                공지사항
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* 메인 콘텐츠 */}
            <div className="sys-main-content">
                <div className="container-fluid">
                    {/* 상태 요약 대시보드 */}
                    <div className="dashboard-section mb-4">
                        <h4 className="section-title mb-3">
                            <i className="bi bi-graph-up me-2"></i>
                            현황 요약
                        </h4>
                        <div className="dashboard-grid">
                            <div className="dashboard-card">
                                <div className="dashboard-icon" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                                    <i className="bi bi-trophy"></i>
                                </div>
                                <div className="dashboard-info">
                                    <div className="dashboard-label">등록된 대회 수</div>
                                    <div className="dashboard-value">{dashboardData.competitionCount}개</div>
                                </div>
                            </div>
                            <div className="dashboard-card">
                                <div className="dashboard-icon" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
                                    <i className="bi bi-people"></i>
                                </div>
                                <div className="dashboard-info">
                                    <div className="dashboard-label">등록된 선수 수</div>
                                    <div className="dashboard-value">{dashboardData.playerCount}명</div>
                                </div>
                            </div>
                            <div className="dashboard-card">
                                <div className="dashboard-icon" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
                                    <i className="bi bi-play-circle"></i>
                                </div>
                                <div className="dashboard-info">
                                    <div className="dashboard-label">진행 중 토너먼트</div>
                                    <div className="dashboard-value">{dashboardData.ongoingTournaments}개</div>
                                </div>
                            </div>
                            <div className="dashboard-card">
                                <div className="dashboard-icon" style={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' }}>
                                    <i className="bi bi-check-circle"></i>
                                </div>
                                <div className="dashboard-info">
                                    <div className="dashboard-label">완료된 대회</div>
                                    <div className="dashboard-value">{dashboardData.completedCompetitions}개</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 주요 액션 / 단축 기능 */}
                    <div className="quick-actions-section mb-4">
                        <h4 className="section-title mb-3">
                            <i className="bi bi-lightning-charge me-2"></i>
                            빠른 작업
                        </h4>
                        <p className="section-description">
                            자주 사용하는 기능에 빠르게 접근하세요.
                        </p>
                        <div className="quick-actions-grid">
                            {quickActions.map((action, index) => (
                                <button
                                    key={index}
                                    className="quick-action-btn"
                                    onClick={() => handleMenuClick(action.path)}
                                    style={{ borderLeftColor: action.color }}
                                >
                                    <div className="quick-action-icon" style={{ background: action.color }}>
                                        <i className={`bi ${action.icon}`}></i>
                                    </div>
                                    <span className="quick-action-label">{action.label}</span>
                                    <i className="bi bi-chevron-right quick-action-arrow"></i>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* 카카오톡 오픈채팅 문의 */}
                    <div className="kakao-section mb-4">
                        <div className="kakao-card">
                            <div className="kakao-header">
                                <div className="inquiry-header-wrapper">
                                    <h4 className="section-title mb-0">
                                        <i className="bi bi-chat-dots-fill me-2"></i>
                                        문의하기
                                    </h4>
                                    <button
                                        className="btn btn-primary chatbot-button"
                                        onClick={() => setChatbotOpen(true)}
                                    >
                                        <i className="bi bi-robot"></i>
                                        <span className="chatbot-button-text">챗봇 질문하기</span>
                                    </button>
                                </div>
                                <div className="chatbot-notice" style={{
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    color: 'white',
                                    padding: '15px 20px',
                                    borderRadius: '10px',
                                    marginBottom: '15px',
                                    textAlign: 'center',
                                    fontSize: '18px',
                                    fontWeight: '700',
                                }}>
                                    <i className="bi bi-stars me-2"></i>
                                    챗봇에게 먼저 질문해보세요!!
                                    <i className="bi bi-stars ms-2"></i>
                                </div>
                                <p className="kakao-description">
                                    시스템 사용 중 궁금한 점이나 문의사항이 있으시면 카카오톡 오픈채팅으로 문의해주세요.
                                </p>
                            </div>
                            <div className="kakao-content">
                                <div className="kakao-image-container">
                                    <img
                                        src="/images/kakao_openchat.png"
                                        alt="카카오톡 오픈채팅 QR코드"
                                        className="kakao-qr-image"
                                    />
                                </div>
                                <div className="kakao-info">
                                    <div className="kakao-features">
                                        <div className="kakao-feature">
                                            <i className="bi bi-check-circle-fill text-success me-2"></i>
                                            <span>실시간 문의 응답</span>
                                        </div>
                                        <div className="kakao-feature">
                                            <i className="bi bi-check-circle-fill text-success me-2"></i>
                                            <span>시스템 사용 가이드</span>
                                        </div>
                                        <div className="kakao-feature">
                                            <i className="bi bi-check-circle-fill text-success me-2"></i>
                                            <span>기술 지원</span>
                                        </div>
                                        <div className="kakao-feature">
                                            <i className="bi bi-check-circle-fill text-success me-2"></i>
                                            <span>개선 사항 제안</span>
                                        </div>
                                    </div>
                                    <div className="kakao-actions">
                                        <a
                                            href="https://invite.kakao.com/tc/3SISslHotX"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-kakao"
                                        >
                                            <i className="bi bi-chat-fill me-2"></i>
                                            카카오톡 오픈채팅 참여하기
                                        </a>
                                        <p className="kakao-note">
                                            <i className="bi bi-info-circle me-1"></i>
                                            위 QR코드를 스캔하거나 버튼을 클릭하여 참여하세요
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 단계별 사용 가이드 */}
                    <div className="steps-section">
                        <h4 className="section-title mb-3">
                            <i className="bi bi-list-check me-2"></i>
                            단계별 사용 가이드
                        </h4>
                        <p className="section-description">
                            토너먼트 운영은 다음 순서로 진행됩니다. 각 단계별 메뉴를 통해 필요한 작업을 수행하세요.
                        </p>
                        <div className="steps-flow">
                            {steps.map((step, index) => (
                                <React.Fragment key={step.step}>
                                    <div className="step-card" style={{ borderTopColor: step.color }}>
                                        <div className="step-header" style={{ background: `linear-gradient(135deg, ${step.color} 0%, ${step.color}dd 100%)` }}>
                                            <div className="step-icon">
                                                <i className={`bi ${step.icon}`}></i>
                                            </div>
                                            <div className="step-info">
                                                <div className="step-number">Step {step.step}</div>
                                                <div className="step-title">{step.title}</div>
                                            </div>
                                        </div>
                                        <div className="step-body">
                                            <p className="step-description">{step.description}</p>
                                            <div className="step-menus">
                                                {step.menus.map((menu, idx) => (
                                                    <button
                                                        key={idx}
                                                        className="step-menu-btn"
                                                        onClick={() => handleMenuClick(menu.path)}
                                                    >
                                                        <i className={`bi ${menu.icon} me-2`}></i>
                                                        {menu.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    {index < steps.length - 1 && (
                                        <div className="step-arrow">
                                            <i className="bi bi-arrow-right"></i>
                                        </div>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>

                </div>
            </div>

            {/* 챗봇 컴포넌트 */}
            <CompChatbot isOpen={chatbotOpen} onClose={() => setChatbotOpen(false)} />
        </div>
    );
};

export default SysContent;
